"use client";

import { useState } from "react";
import { useToast } from "../use-toast.js";

// Public booking specific types
interface PublicBookingData {
  email: string;
  description: string;
  location: string;
  isOnline?: boolean;
  bookeeEmail: string;
  eventType?: string; // Event type for round robin assignment
  slot: {
    start: string;
    end: string;
  };
}

export interface BookingError {
  message: string;
  fieldErrors?: Record<string, string>;
  statusCode?: number;
  rawError?: any;
}

export interface BookingSuccessData {
  eventId: string;
  title?: string;
  description?: string;
  location?: string;
  start?: string;
  end?: string;
  timeZone?: string;
  htmlLink?: string;
  hangoutLink?: string;
  isOnline?: boolean;
  attendees?: Array<{ email: string; name?: string }>;
}

interface UseBookingSubmissionReturn {
  isSubmitting: boolean;
  isLoading: boolean;
  error: BookingError | null;
  submitBooking: (data: PublicBookingData) => Promise<{ success: boolean; eventId?: string; meetingDetails?: BookingSuccessData }>;
}

/**
 * Parse error response from API to extract validation errors
 */
function parseBookingError(response: Response, errorData: any): BookingError {
  const statusCode = response.status;
  const errorMessage = errorData?.error || 'Failed to book meeting';
  
  // Parse validation errors from format: "Validation failed: field1 is required, field2 is required"
  const fieldErrors: Record<string, string> = {};
  let message = errorMessage;
  
  if (errorMessage.includes('Validation failed:')) {
    const validationPart = errorMessage.split('Validation failed:')[1]?.trim();
    if (validationPart) {
      // Split by comma to get individual field errors
      const errors = validationPart.split(',').map((e: string) => e.trim());
      errors.forEach((error: string) => {
        // Extract field name from error like "email is required" or "location is required"
        const match = error.match(/^(\w+)\s+(.+)$/);
        if (match) {
          const [, field, errorText] = match;
          fieldErrors[field] = errorText;
        } else {
          // If we can't parse, use the whole error as a general message
          fieldErrors['general'] = error;
        }
      });
      
      // Create a user-friendly message
      if (Object.keys(fieldErrors).length > 0) {
        const fieldNames = Object.keys(fieldErrors).filter(f => f !== 'general');
        if (fieldNames.length > 0) {
          message = `Please fix the following: ${fieldNames.map(f => f.replace(/([A-Z])/g, ' $1').toLowerCase()).join(', ')}`;
        }
      }
    }
  }
  
  // Handle specific error messages
  if (errorMessage.includes('Location is required')) {
    fieldErrors['location'] = 'Location is required for in-person meetings';
    message = 'Please provide a meeting location';
  }
  
  return {
    message,
    fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined,
    statusCode,
    rawError: errorData
  };
}

export function useBookingSubmission(): UseBookingSubmissionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<any>(null);
  const { toast } = useToast();

  const submitBooking = async (data: PublicBookingData): Promise<{ success: boolean; eventId?: string; meetingDetails?: BookingSuccessData }> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/unifiedtime/public-booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        let errorData: any;
        try {
          errorData = await response.json();
        } catch {
          // If response is not JSON, create a generic error
          errorData = { error: `Request failed with status ${response.status}` };
        }
        
        const parsedError = parseBookingError(response, errorData);
        setError(parsedError);
        
        // Show toast with user-friendly message
        toast({
          title: "Booking Failed",
          description: parsedError.message,
          variant: "destructive",
        });
        
        return { success: false };
      }

      const result = await response.json();
      
      toast({
        title: "Booking Successful",
        description: "Your meeting has been scheduled successfully!",
      });

      return { 
        success: true, 
        eventId: result.eventId,
        meetingDetails: result.meetingDetails
      };
    } catch (err: any) {
      // Handle network errors or other exceptions
      const bookingError: BookingError = {
        message: err.message || "Failed to book meeting. Please check your connection and try again.",
        rawError: err
      };
      setError(bookingError);
      
      toast({
        title: "Booking Failed",
        description: bookingError.message,
        variant: "destructive",
      });
      
      return { success: false };
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    isLoading: isSubmitting,
    error,
    submitBooking,
  };
}
